/**
 * The live "time remaining" on a failed certification's retake window.
 *
 * ── Why it ticks, and why only once a minute ──────────────────────────────────────────────
 *
 * The requirement is that the remaining time continuously reflects the current date and time. A
 * value computed once on mount stops being true the moment the page is left open — and this page
 * is exactly the kind somebody leaves open, because they came to it to think about a deadline.
 *
 * The display stops at minutes, so it re-renders once a minute rather than once a second. A
 * per-second tick would re-render every open countdown sixty times more often to change nothing
 * visible, and would turn a three-month deadline into a stopwatch, which reads as pressure rather
 * than information.
 *
 * ── The first tick is aligned to the minute boundary ──────────────────────────────────────
 *
 * A plain 60-second interval started at, say, 12:00:59 would update at 12:01:59 — a whole minute
 * after the displayed value went stale. Aligning the first timeout to the next real minute keeps
 * the number at most a second behind the clock rather than up to a minute.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  formatRetakeRemaining,
  retakeTimeRemaining,
  retakeUrgency,
} from '../../types/certification';
import './Certifications.css';

interface Props {
  /** The retake deadline as YYYY-MM-DD. */
  deadline?: string;
  /** Compact renders the figure inline, for a table cell. */
  variant?: 'block' | 'inline';
}

const RetakeCountdown: React.FC<Props> = ({ deadline, variant = 'block' }) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!deadline) return;

    let timeoutId: number | undefined;
    let intervalId: number | undefined;

    // Land on the next whole minute, then settle into a steady one-minute beat.
    const msToNextMinute = 60000 - (Date.now() % 60000);
    timeoutId = window.setTimeout(() => {
      setNow(new Date());
      intervalId = window.setInterval(() => setNow(new Date()), 60000);
    }, msToNextMinute);

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [deadline]);

  const remaining = useMemo(() => retakeTimeRemaining(deadline, now), [deadline, now]);
  const urgency = retakeUrgency(remaining);

  if (!remaining) return null;

  return (
    <div className={`cert-countdown cert-countdown--${variant} is-${urgency}`}>
      <span className="cert-countdown__label">Time Remaining</span>
      <span className="cert-countdown__value">{formatRetakeRemaining(remaining)}</span>
      {/*
        * A sentence only when there is something to act on.
        *
        * A deadline three months out needs no urging; one inside a week does, and one that has
        * passed needs to say what that means rather than leaving "Deadline Passed" to be
        * interpreted. Silence in the ordinary case is what makes the exceptional case visible.
        */}
      {urgency === 'critical' && (
        <span className="cert-countdown__note">Retake the exam before the deadline.</span>
      )}
      {urgency === 'expired' && (
        <span className="cert-countdown__note">
          The three-month retake window has closed. Speak to your manager about this
          certification.
        </span>
      )}
    </div>
  );
};

export default RetakeCountdown;
